import ENV from "@/utils/env";
import { api } from "@/lib/api";
import { PreProcessedData } from "../data/types/pre-processed-data.type";

/* Request Types */
export interface UploadFileReq {
  key: string;
  file: File;
  url: string;
}

/* Response Types */
export interface StageUploadResp {
  key: string;
  url: string;
}

export interface StageContextUploadResp {
  id: string;
  url: string;
}

export interface ProcessUploadResp {
  taskId: string;
}

export interface GetUploadStatusResp {
  status: string;
}

export interface StudyPreProcessResp {
  taskIds: string[];
}

export interface StudyAssetStatus {
  assetId: string;
  filename: string;
  status: "RUNNING" | "SUCCEEDED" | "FAILED";
}

/**
 * stageUpload
 *
 * Generates a pre-signed URL to a file in S3.  The URL is valid for one upload and has an expiration.
 * Uses POST instead of GET to avoid Safari's aggressive caching of GET requests.
 *
 * @returns A fully qualified, pre-signed URL.
 */
export const stageUpload = async (projectId: string, studyId: string) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/uploads/stage`);
  return resp.data as StageUploadResp;
};

/**
 * uploadUpload
 *
 * Upload a file to S3 using the pre-signed URL returned by stageFile.
 *
 * @returns It returns the key of the uploaded file.
 */
export const uploadUpload = async ({ key, file, url }: UploadFileReq) => {
  await api.put(url, file, {
    headers: { "Content-Type": file.type },
  });

  return key;
};

/**
 * preProcessUpload
 *
 * This runs the asset through the pipelines to find AOI's and generate pre-processed data.
 * It returns the taskId of the processing task for us to poll the status.
 */
export const preProcessUpload = async (projectId: string, studyId: string, key: string) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/uploads/preprocess`, {
    assets: [{ key }],
  });

  return resp.data as ProcessUploadResp;
};

/**
 * processUpload
 *
 * This processes the uploaded asset and generates the insight data, the backend decides which pipeline to use based on the study type.
 * It returns the taskId of the processing task for us to poll the status.
 */
export const processUpload = async (projectId: string, studyId: string, data: PreProcessedData, contextPackIds?: string[]) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/uploads/process`, {
    contextPackIds,
    assets: [data],
  });

  return resp.data as ProcessUploadResp;
};

/**
 * getUploadStatus
 *
 * This gets the status of the task based on the taskId. We have to poll this endpoint as the task might take some
 * time to complete.
 */
export const getUploadStatus = async (projectId: string, studyId: string, taskId: string) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/uploads/status`, {
    taskId,
  });

  return resp.data as GetUploadStatusResp;
};

/**
 * studyPreProcess
 *
 * Triggers study-level preprocessing for all assets in a study.
 * Used in the studyEdit1 flow where assets are already uploaded at the select step.
 */
export const studyPreProcess = async (projectId: string, studyId: string): Promise<StudyPreProcessResp> => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/preprocess`);
  return resp.data as StudyPreProcessResp;
};

/**
 * studyProcess
 *
 * Triggers study-level processing for all assets in a study.
 * Used in the studyEdit1 flow — no request body needed, just project and study IDs.
 */
export const studyProcess = async (projectId: string, studyId: string): Promise<StudyPreProcessResp> => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/process`);
  return resp.data as StudyPreProcessResp;
};

/**
 * getStudyStatus
 *
 * Polls the status of all assets in a study for a given process type.
 * Returns an array of assets with their current status.
 */
export const getStudyStatus = async (projectId: string, studyId: string, processType: "preprocess" | "process"): Promise<StudyAssetStatus[]> => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/status`, {
    processType,
  });
  return resp.data as StudyAssetStatus[];
};

/*
 * stageContextUpload
 *
 * Generates a pre-signed URL to a file in S3.  The URL is valid for one context upload and has an expiration.
 */
export const stageContextUpload = async (contextPackId: string) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/contexts/stage`, { packId: contextPackId });
  return resp.data as StageContextUploadResp;
};
